import { usePageLinks } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { FilterFunc, NavigationEntity } from '@tunasong/models'
import { elementSort, logger, matchAny } from '@tunasong/models'
import type { LayoutComponent } from '@tunasong/plugin-lib'
import { PerformanceViewContext, getEntityIcon, partFeatures, usePlugins, useRenderContent } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isAudio, isFolderContainer, isPage, isSong } from '@tunasong/schemas'
import type { NavigationOptions, SidebarComponent } from '@tunasong/ui-lib'
import { MetronomeButton } from '@tunasong/audio-ui'
import {
  Button,
  ContentLayout,
  Drawer,
  EntityEventsContainer,
  EntityTitle,
  FullPageProgress,
  Typography,
  getEntityPath,
  useCurrentEntity,
  useNavigate,
  useNavigateToEntityStore,
  usePageTurner,
  useTunaMediaQuery,
} from '@tunasong/ui-lib'
import { useCrisp } from '@tunasong/vendor'
import { usePresenceSignaling } from '@tunasong/ws'
import type { FC } from 'react'
import { useEffect, useMemo, useReducer, useState } from 'react'
import { Folder } from 'react-kawaii'
import { autoscrollHold, initialAutoscrollHold } from './autoscroll-hold.js'
import { useAutoscroll } from './autoscroll.hook.js'
import { useExitToEntryPoint } from './exit-history.hook.js'
import { DisplayPopover } from './display-popover.js'
import { NotesToggle } from './notes-toggle.js'
import PerformanceHeader from './performance-header.js'
import { PartMenu } from './part/part-menu.js'
import { partHeaderLine } from './part/part-header-line.js'
import { PitchFrameToggle } from './part/pitch-frame-toggle.js'
import { PartPrompts } from './part/part-prompts.js'
import { usePerformanceRendering } from './part/performance-rendering.hook.js'
import { usePerformanceHotkeys } from './performance-hotkeys.hook.js'
import { AutoscrollButton, PerformanceBottomBar } from './performance-bottom-bar.js'
import { usePerformanceNotes } from './performance-notes.hook.js'
import { usePerformanceViewStore } from './performance-settings.js'
import { ResizeHandle } from './resize-handle.js'
import { ScrollProgressBar } from './scroll-progress-bar.js'
import { SetNavigation } from './set-navigation.js'
import { setPosition } from './set-position.js'
import { songMeta } from './song-meta.js'
import styles from './performance.module.css'

export interface PerformanceProps extends Pick<LayoutComponent, 'layout'> {
  entity: Persisted<Entity>
  Sidebar?: FC<SidebarComponent>
  filter?: FilterFunc
}

export const Performance: FC<PerformanceProps> = props => {
  const { entity, filter = isSong, Sidebar } = props

  useCrisp({ show: false })

  const { columnFill, columns, fontSize, sidebarWidth, setSidebarWidth, flashBackground } = usePerformanceViewStore()

  /** The part menu is controlled so the R shortcut and "Change for this band" can open it. */
  const [partMenuOpen, setPartMenuOpen] = useState(false)

  // Narrow viewports — phones and resized desktop windows alike — get a bottom-anchored
  // drawer instead of the vertical splitter. The splitter at <600px squashes both lyrics
  // and notes into unusable widths.
  const isTiny = useTunaMediaQuery('(max-width:599.95px)')

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  // The scrollable content element, tracked in state so the scroll-progress bar
  // can subscribe to it. Scroll-progress itself lives in <ScrollProgressBar> so
  // scroll events don't re-render the chord-heavy content here (GH #1549).
  // The state setter is the ref: it is stable, so React attaches it once. A ref callback
  // created per render was detached and re-attached on every commit, and each re-attach
  // queued another full re-render of the content (GH #1549).
  // Never read a ref during render here: passing `ref.current` to useAutoscroll made the React
  // Compiler skip this whole component, so nothing in it was memoized (GH #1549).
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)

  // @todo this should be dynamic based on which entities have the `sys:performance` content view
  const entityFilter = matchAny(isSong, isPage, isAudio)

  // Extract the page's entity links from the decoded value — no editor.
  // `usePageTools` would materialize a full Plate editor + decoded doc held for
  // the whole performance session just to read links (memory-analysis S3, #1654).
  const { entities: linkedEntities } = usePageLinks(entity)
  /** For folders, we include only songs and not pages */
  const { entities: folderEntities } = graphHooks.useEntityChildren({ parentId: entity.id, filter })

  const { entityClips } = graphHooks.useEntityClips(entity?.id)

  // Infer duration from audio attachments or linked audio entities
  const DEFAULT_DURATION = 180
  const duration = useMemo(() => {
    if (isSong(entity) && entity.duration) {
      return entity.duration
    }
    // Find the longest audio attachment in audioClips
    const audioAttachments = entityClips.map(clip => clip.features?.base?.duration)
    // Find the index of the longest audio attachment
    const maxAttachmentDuration = Math.max(...audioAttachments.filter((d): d is number => typeof d === 'number'))
    return Number.isFinite(maxAttachmentDuration) ? maxAttachmentDuration : DEFAULT_DURATION
  }, [entityClips, entity])

  /** Autoscroll */
  const [autoscroll, dispatchAutoscroll] = useReducer(autoscrollHold, initialAutoscrollHold)
  const isAutoscrollPlaying = autoscroll.playing
  const toggleAutoscroll = () => dispatchAutoscroll({ type: 'toggle' })
  /** Any note composer pauses autoscroll while it is open, and resumes it on close if it was running. */
  const holdAutoscroll = () => {
    dispatchAutoscroll({ type: 'hold' })
    return () => dispatchAutoscroll({ type: 'release' })
  }

  const { isScrolling: _isScrolling } = useAutoscroll({
    scrollContainer: scrollEl,
    isPlaying: isAutoscrollPlaying,
    duration,
    onUserScroll: () => dispatchAutoscroll({ type: 'stop' }),
  })

  const entities = isFolderContainer(entity)
    ? [entity, ...elementSort(folderEntities, entity.options?.sort)]
    : [entity, ...linkedEntities.filter(entityFilter)]

  const { currentId, setCurrentId } = useCurrentEntity({ entities, replaceHistory: false })
  const { entity: currentEntity, previousEntity, isFetching } = graphHooks.useEntity(currentId)

  /** Signal that we're looking at the current entity, so we can receive changes even though we're read-only */
  usePresenceSignaling(currentEntity)

  const routerNavigate = useNavigate()

  // Handle navigation to the current entity
  // Select the stable actions: a whole-store subscription re-rendered the layout on its own install below (GH #1549)
  const setCustomNavigateFn = useNavigateToEntityStore(s => s.setCustomNavigateFn)
  const clearCustomNavigateFn = useNavigateToEntityStore(s => s.clearCustomNavigateFn)
  useEffect(() => {
    setCustomNavigateFn((entity?: NavigationEntity, options?: NavigationOptions) => {
      if (!entity?.id) {
        return
      }
      // If there is a change in layout, we should navigate to the entity with the new layout
      if (options?.layout?.toLocaleLowerCase() !== 'performance') {
        /** we cannot use the hook here, since we override the custom navigate Fn, and calling it would recurse */
        void routerNavigate(getEntityPath({ entity, layout: options?.layout }), {
          replace: false,
        })
        return
      }
      // @todo we should probably push the current entity to the history - probably in the useNavigateToEntityStore hook
      setCurrentId(entity.id)
      // "Perform from <song>" on the overview: start the song at its top, as Next does
      dispatchAutoscroll({ type: 'stop' })
      scrollEl?.scrollTo({ top: 0 })
    })
    return () => {
      clearCustomNavigateFn()
    }
  }, [clearCustomNavigateFn, routerNavigate, scrollEl, setCurrentId, setCustomNavigateFn])

  /** Exit restores the history from before performance mode, so browser back does not return to it */
  const handleExit = useExitToEntryPoint(() => {
    void routerNavigate(getEntityPath({ entity, layout: 'default' }), { replace: true })
  })

  const handleChange = (current: Persisted<Entity>) => {
    setCurrentId(current.id)
    dispatchAutoscroll({ type: 'stop' }) // stop autoscroll on entity change
    anchorEl?.scrollTo(0, 0)
  }

  /**
   * Where the performer is in the set. A set may hold a song twice, so the index chosen last wins over the first
   * match of the current id.
   */
  const [expectIdx, setExpectIdx] = useState(-1)
  const current = currentEntity ?? previousEntity
  const foundIdx = current ? entities.findIndex(e => e.id === current.id) : -1
  const currentIdx = expectIdx >= 0 && entities[expectIdx]?.id === current?.id ? expectIdx : Math.max(0, foundIdx)
  const handleSelect = (index: number) => {
    const target = entities[index]
    if (!target) {
      return
    }
    setExpectIdx(index)
    handleChange(target)
  }
  /** Page-turner pedals: the same next and previous as before the bottom bar (they scroll a long song first) */
  usePageTurner({
    container: anchorEl,
    onNext: () => handleSelect(currentIdx + 1),
    onPrevious: () => handleSelect(currentIdx - 1),
  })
  /**
   * A setlist, page or folder of songs opens on its own overview; a song with linked songs is song 1 itself, and a
   * page with no songs is just the page.
   */
  const hasOverview = !isSong(entity) && entities.length > 1
  const position = setPosition({ index: currentIdx, count: entities.length, hasOverview, noun: 'Song' })
  /**
   * A setlist or folder with nothing in it: the stage is its empty overview, so there is nothing to play (GH #2406).
   * A song, page or recording on its own keeps autoscroll, since its own content scrolls.
   */
  const isEmptySet = (entity.type === 'setlist' || isFolderContainer(entity)) && entities.length === 1
  const typeLabel = `${entity.type?.charAt(0).toUpperCase() ?? ''}${entity.type?.slice(1) ?? ''}`
  const eyebrow =
    entities.length <= 1
      ? undefined
      : position.isOverview
        ? `${typeLabel} · Overview`
        : hasOverview
          ? `${entity.name} · ${position.long}`
          : position.long

  const sidebar = Sidebar && currentEntity && (
    <Sidebar
      className={styles.sidebar}
      entity={currentEntity}
      onEntityChange={() => logger.info('Performance sidebar entity change not implemented')}
    />
  )

  const displayEntity = currentEntity ?? previousEntity

  /** My notes: on or off (remembered per user, never opened on their own), beside the song or in a panel. */
  const { notes, showPanel, setVisible, requestNote, setSongNotesSlot } = usePerformanceNotes({
    isTiny,
    entity: displayEntity,
    scrollEl,
    holdAutoscroll,
  })
  const showSidebar = notes.visible
  usePerformanceHotkeys({
    onPartMenu: () => setPartMenuOpen(true),
    onToggleNotes: () => setVisible(!showSidebar),
    onNote: () => requestNote('key'),
  })

  /** The parts for the item in view: inside a setlist, each song's own band decides them. */
  const { performanceParts, view } = usePerformanceRendering(displayEntity, notes)
  /** One plugin set: the parts reach the plugins through PerformanceViewContext, so a part change never remounts the song. */
  const plugins = usePlugins('performance')

  /** Plugins must specify a "performance" content view to show in the performance mode */
  const { render } = useRenderContent({ contentView: 'sys:performance', plugins })

  /**
   * GH #1610 (CRITICAL): keep the last-known entity rendered during a refetch
   * so an already-open song never blanks to `<FullPageProgress/>`. `currentEntity`
   * (RTK `currentData`) goes `undefined` while the entity container is re-fetched
   * — which a peer's edit triggers (cache invalidation + a freshly-signed storage
   * URL). `previousEntity` (RTK `data`) retains the last value. Because the inner
   * editor is keyed on `entity.id`, rendering the retained (same-id) entity keeps
   * it mounted and its YDoc connection alive, so peer changes flow through the
   * YDoc instead of forcing a full editor reload with the progress bar.
   */
  const performanceContent = displayEntity ? (
    <>
      {/* Song notes and the "My notes" heading, portalled here by the personal-note plugin */}
      <div ref={setSongNotesSlot} />
      <EntityTitle entity={displayEntity} readOnly={true} />
      {render(displayEntity)}
    </>
  ) : isFetching ? (
    <FullPageProgress />
  ) : (
    <div className={styles.emptyState}>
      <Folder size={200} mood="sad" color={'var(--tuna-palette-secondary-main)'} />
      <Typography variant="caption">This entity does not have any content with a performance view</Typography>
    </div>
  )

  if (!plugins) {
    return null
  }

  const content = (
    <>
      <ContentLayout
        ref={setScrollEl}
        columns={columns}
        columnFill={columnFill}
        className={styles.contentLayout}
        size={fontSize}
        style={{
          // Right padding reserves room for the vertical section labels rendered
          // in the right-side block gutter (positioned 36px outside each block via
          // .gutter--right). Without it, the gutter overflows ContentLayout and the
          // themed scrollbar reveals an unwanted horizontal scroll on scroll.
          // Phones use the full width (GH #1553): the gutter narrows to 20px there
          // (block-gutter.module.css), which the 8px ContentPaper padding plus 12px here covers.
          padding: isTiny
            ? 'var(--tuna-spacing-1) var(--tuna-spacing-1_5) var(--tuna-spacing-1) 0'
            : '8px 40px 8px 8px',
          width: '100%',
          fontSize: fontSize === 'small' ? '1rem' : fontSize === 'medium' ? '1.2rem' : '1.5rem',
        }}
      >
        <PerformanceViewContext value={view}>
          <EntityEventsContainer entity={displayEntity}>{performanceContent}</EntityEventsContainer>
        </PerformanceViewContext>
      </ContentLayout>
    </>
  )

  const metronome =
    isSong(displayEntity) && displayEntity.rhythm ? (
      <MetronomeButton
        size="medium"
        rhythm={displayEntity.rhythm}
        flashBackground={flashBackground}
        aria-label={`Metronome, ${displayEntity.rhythm.tempo} BPM`}
      />
    ) : null

  return (
    <div className={styles.root}>
      <PerformanceHeader
        eyebrow={eyebrow}
        title={displayEntity?.name ?? entity.name}
        onExit={handleExit}
        compact={isTiny}
        progress={<ScrollProgressBar scrollEl={scrollEl} />}
        partMenu={
          isSong(displayEntity) ? (
            <PartMenu performanceParts={performanceParts} open={partMenuOpen} onOpenChange={setPartMenuOpen} />
          ) : null
        }
        notesToggle={currentEntity ? <NotesToggle entity={currentEntity} compact={isTiny} /> : null}
        pitchFrame={
          isSong(displayEntity) ? (
            <PitchFrameToggle
              parts={view.parts}
              capo={view.pitch.capo}
              frame={performanceParts.frame ?? 'shapes'}
              onChange={performanceParts.setFrame}
            />
          ) : null
        }
        partLine={
          isSong(displayEntity)
            ? partHeaderLine({
                parts: view.parts,
                song: displayEntity,
                frame: performanceParts.frame,
                transpose: performanceParts.transpose,
              })
            : undefined
        }
        display={
          <DisplayPopover
            showChordsToggle={!partFeatures(view.parts, { showChords: false }).chordsShown}
            compact={isTiny}
          />
        }
      />
      {isSong(displayEntity) ? (
        <PartPrompts
          performanceParts={performanceParts}
          onOpenPartMenu={() => setPartMenuOpen(true)}
          noticeClassName={styles.defaultPartNotice}
        />
      ) : null}

      <div ref={setAnchorEl} className={styles.contentRow}>
        <div
          className={isTiny ? `${styles.contentPane} ${styles['contentPane--tiny']}` : styles.contentPane}
          style={{ flexBasis: showPanel && !isTiny ? `${100 - sidebarWidth}%` : '100%' }}
        >
          {content}
        </div>
        {showPanel && currentEntity && !isTiny ? (
          <>
            <ResizeHandle sidebarWidth={sidebarWidth} setSidebarWidth={setSidebarWidth} />
            <div className={styles.sidebarPane} style={{ flexBasis: `${sidebarWidth}%` }}>
              {sidebar}
            </div>
          </>
        ) : null}
      </div>
      <PerformanceBottomBar
        navigation={
          <SetNavigation
            label="Set"
            noun="song"
            items={entities.map(e => {
              const Icon = getEntityIcon(e, plugins)
              return { id: e.id, name: e.name ?? 'Untitled', meta: songMeta(e), icon: <Icon /> }
            })}
            index={currentIdx}
            hasOverview={hasOverview}
            onSelect={handleSelect}
            compact={isTiny}
          />
        }
      >
        {/* The overview is a list, and an empty set has nothing in it: nothing to scroll in time */}
        {position.isOverview || isEmptySet ? null : (
          <AutoscrollButton
            isPlaying={isAutoscrollPlaying}
            duration={duration}
            onToggle={toggleAutoscroll}
            compact={isTiny}
          />
        )}
        {metronome}
        {isTiny && currentEntity ? (
          <Button variant="text" color="inherit" onClick={() => requestNote('jot')}>
            + Jot
          </Button>
        ) : null}
      </PerformanceBottomBar>
      {/* A page on a phone has no sections to sit beside: its notes keep the bottom panel */}
      {isTiny && currentEntity ? (
        <Drawer
          open={showPanel}
          onClose={() => setVisible(false)}
          anchor="bottom"
          variant="persistent"
          width="70vh"
          className={styles.mobileDrawer}
        >
          {sidebar}
        </Drawer>
      ) : null}
    </div>
  )
}
